import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CreditCard, Landmark, Check, Loader2, Crown, Copy, ShieldCheck,
} from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function Plans() {
  const [packages, setPackages] = useState([]);
  const [mine, setMine] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null); // `${id}-stripe` | `${id}-bank`
  const [bank, setBank] = useState(null);
  const [params, setParams] = useSearchParams();

  const loadMine = useCallback(() => api.get("/payments/me").then((r) => setMine(r.data)).catch((err) => console.debug("payments/me failed:", err)), []);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch((err) => console.error("Error cargando paquetes:", err));
    loadMine();
  }, [loadMine]);

  // Poll payment status on return from Stripe
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      if (tries > 8) { toast.error("No pudimos confirmar el pago. Si se cobró, contáctanos."); params.delete("session_id"); setParams(params, { replace: true }); return; }
      tries += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("¡Pago confirmado! Tu plan está activo. 🎉");
          loadMine();
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("La sesión de pago expiró.");
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
      } catch (err) { console.debug("checkout status poll error:", err); }
      setTimeout(poll, 2500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payStripe = async (id) => {
    setLoadingPlan(`${id}-stripe`);
    try {
      const { data } = await api.post("/payments/checkout/session", { plan_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoadingPlan(null);
    }
  };

  const payBank = async (id) => {
    setLoadingPlan(`${id}-bank`);
    try {
      const { data } = await api.post("/payments/bank-transfer", { plan_id: id });
      setBank(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoadingPlan(null);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };
  const currentPlan = mine?.plan;

  return (
    <div data-testid="plans-page">
      <div className="flex items-center gap-3 mb-2">
        <Crown className="text-yellow-400" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Paquetes y Suscripción</h1>
      </div>
      <p className="text-zinc-400 mb-6">Activa tu paquete para desbloquear todo el poder de la IA.</p>

      {currentPlan && (
        <div data-testid="current-plan" className="mb-8 rounded-2xl border border-green-500/30 bg-green-500/5 p-5 flex items-center gap-3">
          <ShieldCheck className="text-green-400" size={22} />
          <div>
            <div className="font-semibold text-green-300">Paquete activo: {packages.find((p) => p.id === currentPlan)?.name || currentPlan}</div>
            <div className="text-xs text-zinc-400">Estado: {mine?.plan_status === "active" ? "Activo" : mine?.plan_status}</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-6 items-stretch">
        {packages.map((pkg) => (
          <div key={pkg.id} data-testid={`plan-${pkg.id}`} className={`relative rounded-2xl border p-6 flex flex-col w-full max-w-md ${pkg.popular ? "border-yellow-400 bg-yellow-400/5" : "border-white/10 bg-[#121212]"}`}>
            {pkg.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Más popular</span>}
            <h3 className="font-display font-bold text-2xl">{pkg.name}</h3>
            <p className="text-sm text-zinc-400 mt-1">{pkg.tagline}</p>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display font-black text-4xl">${Math.round(pkg.price)}</span>
              <span className="text-zinc-500 mb-1">{pkg.currency}/{pkg.period}</span>
            </div>
            <ul className="mt-5 space-y-2 flex-1">
              {pkg.features.map((f, i) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={15} className={`mt-0.5 shrink-0 ${pkg.popular ? "text-yellow-400" : "text-purple-500"}`} /> {f}</li>
              ))}
            </ul>
            <div className="mt-6 space-y-2">
              <Button data-testid={`pay-stripe-${pkg.id}`} onClick={() => payStripe(pkg.id)} disabled={loadingPlan === `${pkg.id}-stripe`} className={`w-full rounded-xl py-6 font-bold ${pkg.popular ? "bg-yellow-400 hover:bg-yellow-300 text-black" : "bg-purple-600 hover:bg-purple-500 text-white"}`}>
                {loadingPlan === `${pkg.id}-stripe` ? <Loader2 className="animate-spin mr-2" size={16} /> : <CreditCard size={16} className="mr-2" />} Pagar con tarjeta
              </Button>
              <Button data-testid={`pay-bank-${pkg.id}`} onClick={() => payBank(pkg.id)} disabled={loadingPlan === `${pkg.id}-bank`} variant="outline" className="w-full rounded-xl py-6 border-white/15 bg-transparent hover:bg-white/5">
                {loadingPlan === `${pkg.id}-bank` ? <Loader2 className="animate-spin mr-2" size={16} /> : <Landmark size={16} className="mr-2" />} Transferencia bancaria
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!bank} onOpenChange={(o) => !o && setBank(null)}>
        <DialogContent className="bg-[#0d0d0d] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Landmark className="text-yellow-400" size={22} /> Transferencia bancaria</DialogTitle>
          </DialogHeader>
          {bank && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">Realiza tu transferencia por <span className="text-yellow-impact font-bold">${Math.round(bank.amount)} USD</span> (plan {bank.plan}) con los siguientes datos:</p>
              <div className="rounded-xl bg-black border border-white/10 p-4 space-y-3 text-sm">
                <Row label="Beneficiario" value={bank.bank.beneficiario} onCopy={() => copy(bank.bank.beneficiario)} />
                <Row label="Banco" value={bank.bank.banco} onCopy={() => copy(bank.bank.banco)} />
                <Row label="CLABE" value={bank.bank.clabe} onCopy={() => copy(bank.bank.clabe)} />
                <Row label="Referencia" value={bank.reference} onCopy={() => copy(bank.reference)} />
              </div>
              <p className="text-xs text-zinc-500">Importante: incluye la <strong className="text-zinc-300">referencia</strong> en tu transferencia y envíanos tu comprobante. Tu plan se activará una vez que confirmemos el pago.</p>
              <Button onClick={() => { setBank(null); toast.success("Datos guardados. Activaremos tu plan al confirmar la transferencia."); }} className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl py-6">
                Ya realicé mi transferencia
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value, onCopy }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="text-zinc-200 font-mono break-all">{value}</div>
    </div>
    <button onClick={onCopy} className="shrink-0 text-zinc-400 hover:text-white"><Copy size={16} /></button>
  </div>
);
